/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { decisionItem, decisionList, taskItem, taskList } from '@atlaskit/adf-schema';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { MAX_INDENTATION_LEVEL } from '@atlaskit/editor-common/indentation';
import { toolbarInsertBlockMessages as insertBlockMessages } from '@atlaskit/editor-common/messages';
import { IconAction, IconDecision } from '@atlaskit/editor-common/quick-insert';
import { getListTypes, insertTaskDecisionAction, insertTaskDecisionCommand } from './commands';
import { getCurrentIndentLevel, getTaskItemIndex, isInsideTask } from './pm-plugins/helpers';
import inputRulePlugin from './pm-plugins/input-rules';
import keymap, { getIndentCommand, getUnindentCommand } from './pm-plugins/keymaps';
import { createPlugin } from './pm-plugins/main';
import { stateKey as taskPluginKey } from './pm-plugins/plugin-key';
import ToolbarDecision from './ui/ToolbarDecision';
import ToolbarTask from './ui/ToolbarTask';
var taskDecisionToolbarGroupStyles = css({
  display: 'flex'
});
var addItem = function addItem(insert, listType, schema) {
  return function (_ref) {
    var listLocalId = _ref.listLocalId,
      itemLocalId = _ref.itemLocalId;
    var _getListTypes = getListTypes(listType, schema),
      list = _getListTypes.list,
      item = _getListTypes.item;
    return insert(list.createChecked({
      localId: listLocalId
    }, item.createChecked({
      localId: itemLocalId
    })));
  };
};
export var tasksAndDecisionsPlugin = function tasksAndDecisionsPlugin(_ref2) {
  var _api$analytics, _api$analytics2, _api$analytics3;
  var _ref2$config = _ref2.config,
    _ref2$config2 = _ref2$config === void 0 ? {} : _ref2$config,
    allowNestedTasks = _ref2$config2.allowNestedTasks,
    consumeTabs = _ref2$config2.consumeTabs,
    useLongPressSelection = _ref2$config2.useLongPressSelection,
    api = _ref2.api;
  var getIdentifierProvider = function getIdentifierProvider() {
    var _api$contextIdentifie;
    return api === null || api === void 0 || (_api$contextIdentifie = api.contextIdentifier) === null || _api$contextIdentifie === void 0 || (_api$contextIdentifie = _api$contextIdentifie.sharedState.currentState()) === null || _api$contextIdentifie === void 0 ? void 0 : _api$contextIdentifie.contextIdentifierProvider;
  };
  return {
    name: 'taskDecision',
    nodes: function nodes() {
      return [{
        name: 'decisionList',
        node: decisionList
      }, {
        name: 'decisionItem',
        node: decisionItem
      }, {
        name: 'taskList',
        node: taskList
      }, {
        name: 'taskItem',
        node: taskItem
      }];
    },
    getSharedState: function getSharedState(editorState) {
      if (!editorState) {
        return undefined;
      }
      var pluginState = taskPluginKey.getState(editorState);
      var indentLevel = getCurrentIndentLevel(editorState.selection) || 0;
      var itemIndex = getTaskItemIndex(editorState);
      return {
        focusedTaskItemLocalId: (pluginState === null || pluginState === void 0 ? void 0 : pluginState.focusedTaskItemLocalId) || null,
        isInsideTask: isInsideTask(editorState),
        indentDisabled: itemIndex === 0 || indentLevel >= MAX_INDENTATION_LEVEL,
        outdentDisabled: indentLevel <= 1
      };
    },
    actions: {
      insertTaskDecision: insertTaskDecisionCommand(api === null || api === void 0 || (_api$analytics = api.analytics) === null || _api$analytics === void 0 ? void 0 : _api$analytics.actions, getIdentifierProvider),
      indentTaskList: getIndentCommand(api === null || api === void 0 || (_api$analytics2 = api.analytics) === null || _api$analytics2 === void 0 ? void 0 : _api$analytics2.actions),
      outdentTaskList: getUnindentCommand(api === null || api === void 0 || (_api$analytics3 = api.analytics) === null || _api$analytics3 === void 0 ? void 0 : _api$analytics3.actions)
    },
    pmPlugins: function pmPlugins() {
      return [{
        name: 'tasksAndDecisions',
        plugin: function plugin(_ref3) {
          var portalProviderAPI = _ref3.portalProviderAPI,
            providerFactory = _ref3.providerFactory,
            eventDispatcher = _ref3.eventDispatcher,
            dispatch = _ref3.dispatch;
          return createPlugin(portalProviderAPI, eventDispatcher, providerFactory, dispatch, api, useLongPressSelection);
        }
      }, {
        name: 'tasksAndDecisionsInputRule',
        plugin: function plugin(_ref4) {
          var _api$analytics4;
          var schema = _ref4.schema,
            featureFlags = _ref4.featureFlags;
          return inputRulePlugin(api === null || api === void 0 || (_api$analytics4 = api.analytics) === null || _api$analytics4 === void 0 ? void 0 : _api$analytics4.actions, getIdentifierProvider)(schema, featureFlags);
        }
      }, {
        name: 'tasksAndDecisionsKeyMap',
        plugin: function plugin(_ref5) {
          var schema = _ref5.schema;
          return keymap(schema, api, allowNestedTasks, consumeTabs);
        }
      } // Needs to be after "save-on-enter"
      ];
    },
    secondaryToolbarComponent: function secondaryToolbarComponent(_ref6) {
      var editorView = _ref6.editorView,
        disabled = _ref6.disabled;
      return jsx("div", {
        css: taskDecisionToolbarGroupStyles
      }, jsx(ToolbarDecision, {
        editorView: editorView,
        isDisabled: disabled,
        isReducedSpacing: true,
        editorAPI: api
      }), jsx(ToolbarTask, {
        editorView: editorView,
        isDisabled: disabled,
        isReducedSpacing: true,
        editorAPI: api
      }));
    },
    pluginsOptions: {
      quickInsert: function quickInsert(_ref7) {
        var formatMessage = _ref7.formatMessage;
        return [{
          id: 'action',
          title: formatMessage(insertBlockMessages.action),
          description: formatMessage(insertBlockMessages.actionDescription),
          priority: 100,
          keywords: ['checkbox', 'task', 'todo'],
          keyshortcut: '[]',
          icon: function icon() {
            return jsx(IconAction, null);
          },
          action: function action(insert, state) {
            var _api$analytics5;
            return insertTaskDecisionAction(api === null || api === void 0 || (_api$analytics5 = api.analytics) === null || _api$analytics5 === void 0 ? void 0 : _api$analytics5.actions, getIdentifierProvider)(state, 'taskList', INPUT_METHOD.QUICK_INSERT, addItem(insert, 'taskList', state.schema));
          }
        }, {
          id: 'decision',
          title: formatMessage(insertBlockMessages.decision),
          description: formatMessage(insertBlockMessages.decisionDescription),
          priority: 900,
          keyshortcut: '<>',
          icon: function icon() {
            return jsx(IconDecision, null);
          },
          action: function action(insert, state) {
            var _api$analytics6;
            return insertTaskDecisionAction(api === null || api === void 0 || (_api$analytics6 = api.analytics) === null || _api$analytics6 === void 0 ? void 0 : _api$analytics6.actions, getIdentifierProvider)(state, 'decisionList', INPUT_METHOD.QUICK_INSERT, addItem(insert, 'decisionList', state.schema));
          }
        }];
      }
    }
  };
};